import { DefinitionTooltip, Tag } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { EntityLink } from 'components/entity-link';
import type { ColumnType } from 'components/data-table';
import { EMPTY_CELL } from 'utils/empty-cell';
import {
  ACTION_OUTCOME_LABELS,
  ACTION_SOURCE_LABELS,
  fieldLabel,
  labelFor,
  ROLE_LABELS,
} from './utils';
import type { CleanupActorType, CleanupLogRow } from './types';
import { DATETIME_COLUMN_WIDTH } from '../utils';
import shared from '../admin-log.module.scss';

type Col = ColumnDef<CleanupLogRow>;
type Field = keyof CleanupLogRow;

const ACTOR_TAG: Record<CleanupActorType, 'gray' | 'cyan' | 'cool-gray'> = {
  User: 'gray',
  Agent: 'cyan',
  System: 'cool-gray',
};

const dash = <span className={shared.muted}>{EMPTY_CELL}</span>;

/**
 * A column the DataTable formats from its type: em dash for a blank, local
 * time for a datetime, grouped digits for an integer.
 * @param {Field} id - The row field
 * @param {ColumnType} type - How the DataTable formats it
 * @param {number} size - The column width
 * @return {Col} The column
 */
function typed(id: Field, type: ColumnType, size = 150): Col {
  return { id, accessorKey: id, header: fieldLabel(id), size, meta: { type } };
}

/**
 * A text column whose value is derived from the row, such as a label.
 * @param {Field} id - The row field it shows
 * @param {(row: CleanupLogRow) => string | null} value - The shown text
 * @param {number} size - The column width
 * @return {Col} The column
 */
function derived(
  id: Field,
  value: (row: CleanupLogRow) => string | null,
  size = 150
): Col {
  return {
    id,
    accessorFn: value,
    header: fieldLabel(id),
    size,
    meta: { type: 'text' },
  };
}

/**
 * A yes/no column.
 * @param {Field} id - The row field
 * @param {number} size - The column width
 * @return {Col} The column
 */
function yesNo(id: Field, size = 120): Col {
  return derived(id, (row) => (row[id] ? 'Yes' : 'No'), size);
}

/**
 * An entity column: the name, linked to the entity's page.
 * @param {Field} id - The name field
 * @param {(row: CleanupLogRow) => number | null} entityId - The entity's id
 * @return {Col} The column
 */
function entity(
  id: Field,
  entityId: (row: CleanupLogRow) => number | null
): Col {
  return {
    id,
    accessorKey: id,
    header: fieldLabel(id),
    size: 260,
    cell: (info) => (
      <EntityLink
        id={entityId(info.row.original)}
        name={info.getValue() as string | null}
        fallback={dash}
      />
    ),
  };
}

/**
 * A short-label column shown as a tag.
 * @param {Field} id - The row field
 * @param {(value: string) => 'gray' | 'cyan' | 'cool-gray' | 'blue'} tagType - The tag colour for a value
 * @param {number} size - The column width
 * @return {Col} The column
 */
function tagged(
  id: Field,
  tagType: (value: string) => 'gray' | 'cyan' | 'cool-gray' | 'blue',
  size: number
): Col {
  return {
    id,
    accessorKey: id,
    header: fieldLabel(id),
    size,
    cell: (info) => {
      const value = info.getValue() as string | null;
      return value ? (
        <Tag type={tagType(value)} size="sm">
          {value}
        </Tag>
      ) : (
        dash
      );
    },
  };
}

const countyColumn: Col = {
  id: 'county',
  // A string, not the array: the table stacks and centers an array cell, and
  // this cell draws the list on one line.
  accessorFn: (row) => row.county.join(', '),
  header: fieldLabel('county'),
  size: 190,
  // A truncating cell would clip the "+N" popover.
  meta: { overflowVisible: true },
  cell: (info) => {
    const counties = info.row.original.county;
    if (counties.length === 0) return dash;
    const [first, ...rest] = counties;
    return (
      <span>
        {first}{' '}
        {rest.length > 0 && (
          <DefinitionTooltip definition={rest.join(', ')} openOnHover>
            +{rest.length}
          </DefinitionTooltip>
        )}
      </span>
    );
  },
};

export const CLEANUP_COLUMNS: Col[] = [
  typed('actionAt', 'datetime', DATETIME_COLUMN_WIDTH),
  typed('actor', 'text', 170),
  tagged(
    'actorType',
    (actorType) => ACTOR_TAG[actorType as CleanupActorType],
    120
  ),
  tagged('appContext', () => 'blue', 150),
  derived('actionOutcome', (row) =>
    labelFor(ACTION_OUTCOME_LABELS, row.actionOutcome)
  ),
  derived('actionSource', (row) =>
    labelFor(ACTION_SOURCE_LABELS, row.actionSource)
  ),
  entity('entityName', (row) => row.entityId),
  typed('entityId', 'text', 120),
  typed('entityType', 'text', 120),
  typed('entityCreatedAt', 'datetime', DATETIME_COLUMN_WIDTH),
  entity('survivorName', (row) => row.survivorId),
  typed('survivorId', 'text', 170),
  typed('survivorType', 'text', 180),
  derived('role', (row) => ROLE_LABELS[row.role], 140),
  countyColumn,
  {
    id: 'confidence',
    accessorKey: 'confidence',
    header: fieldLabel('confidence'),
    size: 150,
    // A 0–1 fraction shown as a whole percent, as formatConfidence does.
    meta: { type: 'percent', format: { multiplier: 100 } },
  },
  yesNo('reversed'),
  typed('undoneAt', 'datetime', DATETIME_COLUMN_WIDTH),
  typed('undoneBy', 'text', 180),
];
